/**
 * The last daily integrity-check verdict (T-6.15, `GET /audit/integrity`) as a notice.
 * Shared by the audit export screen and the firm-wide audit log (F-58).
 */
import type { JSX } from 'react';

import { ErrorState, Notice, SkeletonText } from '../ui/index.ts';
import { useAuditIntegrity } from '../../hooks/use-audit.ts';
import { getErrorMessage, type AuditIntegrityResponse } from '../../lib/api.ts';
import { getRequestId } from '../../lib/request-id.ts';
import { formatDate } from '../../lib/format/date.ts';

import '../../styles/audit.css';

function checkedAt(verdict: AuditIntegrityResponse): string {
  return verdict.checked_at
    ? formatDate(verdict.checked_at, { withTime: true })
    : 'an unknown time';
}

export function IntegrityVerdict({ verdict }: { verdict: AuditIntegrityResponse }): JSX.Element {
  if (verdict.status === 'never_run') {
    return (
      <Notice role="status">
        The daily integrity check has not run yet. It runs at 03:00 UTC every day.
      </Notice>
    );
  }
  if (verdict.ok) {
    return (
      <Notice tone="success">
        <span className="argus-audit-verdict">
          {`PASSED — ${String(verdict.events_verified)} events re-hashed clean, checked ${checkedAt(verdict)}`}
        </span>
      </Notice>
    );
  }
  return (
    <Notice tone="warning">
      <span className="argus-audit-verdict">
        {`FAILED at sequence ${String(verdict.first_tamper_sequence)}, checked ${checkedAt(verdict)} — incident response is running`}
      </span>
    </Notice>
  );
}

/** Fetches the verdict and renders it with its loading / error states (section 13). */
export function IntegrityStatus(): JSX.Element {
  const integrity = useAuditIntegrity();
  if (integrity.isPending) return <SkeletonText lines={2} label="Loading the last verdict" />;
  if (integrity.isError) {
    return (
      <ErrorState
        title="Could not load the integrity status."
        message={getErrorMessage(integrity.error)}
        requestId={getRequestId(integrity.error)}
        onRetry={() => void integrity.refetch()}
        retrying={integrity.isFetching}
      />
    );
  }
  return <IntegrityVerdict verdict={integrity.data} />;
}
